import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../../../atoms/alert/alert.service';
import { CurrentUserService } from '../../../common/current-user/current-user.service';
import { FirebaseService } from '../../../common/firebase/firebase.service';
import { CreateBusinessAccountDto, CreateUserDto, UserRolesEnum } from '../../../common/general-types/users.dto';
import { MarketingAttributionService } from '../../../services/analytics/marketing-attribution';
import { PosthogService } from '../../../services/analytics/posthog.service';
import { GaService } from '../../../services/ga/ga';
import { mirrorBrandImages, S3uploaderService } from '../../../services/s3uploader/s3uploader.service';
import { ONBOARDING_FLOW, onboardingStepIndex } from '../../../services/ga/onboarding.analytics';
import { BrandFetchService, BrandSuggestionResponse } from '../../onboarding/service/brand-fetch/brand-fetch.service';
import { isBlockedEmailDomain } from '../../onboarding-v2/service/slick-onboard/slick-onboard.service';
import {
  CampaignCurrency,
  DisplayCurrency,
  VALUE_FIRST_STORAGE_KEY,
  VALUE_FIRST_VARIANT,
  ValueFirstBrand,
  ValueFirstDraft,
  ValueFirstPath,
  ValueFirstStep,
  accountFieldError,
  brandFromBrandfetch,
  brandFromSuggestion,
  detectCampaignCurrency,
  emptyDraft,
  isValidWorkEmail,
  normalizeDomain,
  previousStep,
  progressIndex,
  readDraft,
  splitPersonName,
  stepAfterRefresh,
} from './value-first-onboarding.types';
import { AdConversionsService } from '@/app/services/analytics/ad-conversions.service';

export type RegistrationResult = 'ok' | 'exists' | 'error' | 'need-name';

interface GoogleUser {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ValueFirstOnboardingService {
  readonly draft = signal<ValueFirstDraft>(emptyDraft());
  readonly error = signal('');
  readonly finding = signal(false);
  readonly sendingCode = signal(false);
  readonly verifying = signal(false);
  readonly registering = signal(false);
  readonly resendCooldown = signal(0);
  readonly displayCurrency = signal<DisplayCurrency | null>(null);
  readonly turnstileToken = signal<string | null>(null);
  readonly verificationToken = signal<string | null>(null);
  readonly googleUid = signal<string | null>(null);
  readonly googlePhotoUrl = signal('');

  readonly step = computed(() => this.draft().step);
  readonly progress = computed(() => progressIndex(this.draft().step));
  readonly showBack = computed(() => this.draft().step !== 'path');
  readonly brandName = computed(() => this.draft().brand?.name || this.fallbackBrandName());
  readonly activeCurrency = computed<CampaignCurrency>(() => {
    const chosen = this.draft().currency;
    if (chosen) {
      return chosen;
    }
    const brand = this.draft().brand;
    const domain = brand?.domain || normalizeDomain(this.draft().url);
    return detectCampaignCurrency(domain, brand?.countryCode).currency;
  });

  private cooldownTimer: ReturnType<typeof setInterval> | null = null;

  private readonly http = inject(HttpClient);
  private readonly adConversions = inject(AdConversionsService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly brandFetch = inject(BrandFetchService);
  private readonly firebase = inject(FirebaseService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly ga = inject(GaService);
  private readonly posthog = inject(PosthogService);
  private readonly marketingAttribution = inject(MarketingAttributionService);
  private readonly alert = inject(AlertService);
  private readonly uploader = inject(S3uploaderService);

  setDisplayCurrency(currency: DisplayCurrency): void {
    this.displayCurrency.set(currency);
  }

  setTurnstileToken(token: string | null): void {
    this.turnstileToken.set(token);
  }

  clearError(): void {
    this.error.set('');
  }

  startAdEntry(): void {
    this.verificationToken.set(null);
    this.googleUid.set(null);
    this.clearCooldown();
    this.error.set('');
    this.commit({ ...emptyDraft(), step: 'website', path: 'brand' });
  }

  restore(): void {
    const stored = readDraft(this.sessionStorage());
    if (!stored) {
      this.commit(emptyDraft(), false);
    } else {
      const step = stepAfterRefresh(stored);
      this.verificationToken.set(null);
      this.googleUid.set(null);
      this.commit({ ...stored, step, authMode: step === 'email' ? 'email' : stored.authMode }, false);
    }
    this.trackStepView(this.draft().step);
  }

  goBack(): void {
    const next = previousStep(this.draft().step, this.draft().authMode);
    if (!next) {
      return;
    }
    this.error.set('');
    this.commit({ ...this.draft(), step: next });
  }

  selectPath(path: ValueFirstPath): void {
    this.track('onboarding_path_selected', {
      variant: VALUE_FIRST_VARIANT,
      onboarding_type: path,
    });
    if (path === 'agency') {
      this.commit({ ...this.draft(), path });
      void this.router.navigate(['/onboarding-value-first-agency']);
      return;
    }
    if (path === 'creator') {
      this.commit({ ...this.draft(), path });
      void this.router.navigate(['/onboarding-creator']);
      return;
    }
    this.error.set('');
    this.commit({ ...this.draft(), path: 'brand', step: 'website' });
  }

  setUrl(url: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), url }, false);
  }

  setEmail(email: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), email }, false);
  }

  setName(firstName: string, lastName: string): void {
    this.error.set('');
    this.commit({ ...this.draft(), firstName, lastName }, false);
  }

  setCurrency(currency: CampaignCurrency): void {
    if (this.draft().currency !== currency) {
      this.track('currency_changed', {
        variant: VALUE_FIRST_VARIANT,
        currency,
      });
    }
    this.commit({ ...this.draft(), currency }, false);
  }

  openManual(): void {
    this.track('manual_fallback', {
      variant: VALUE_FIRST_VARIANT,
      reason: 'no_website',
    });
    this.error.set('');
    this.commit({ ...this.draft(), step: 'manual' });
  }

  editBrand(): void {
    this.error.set('');
    this.commit({ ...this.draft(), step: 'manual' });
  }

  rejectBrand(): void {
    this.track('not_my_brand', { variant: VALUE_FIRST_VARIANT });
    this.error.set('');
    this.commit({ ...this.draft(), url: '', brand: null, currency: null, step: 'website' });
  }

  claimWorkspace(): void {
    this.error.set('');
    this.commit({ ...this.draft(), currency: this.activeCurrency(), step: 'email' });
  }

  toggleGoal(goal: string): void {
    const goals = this.draft().goals.includes(goal)
      ? this.draft().goals.filter((item) => item !== goal)
      : [...this.draft().goals, goal];
    this.commit({ ...this.draft(), goals }, false);
  }

  async findBrand(): Promise<void> {
    const domain = normalizeDomain(this.draft().url);
    if (!domain) {
      this.error.set('Enter your website to continue.');
      return;
    }
    this.finding.set(true);
    this.error.set('');
    this.commit({ ...this.draft(), url: domain }, false);
    try {
      const suggestions = await firstValueFrom(
        this.brandFetch.brandSearch(domain).pipe(catchError(() => of([] as BrandSuggestionResponse[]))),
      );
      const best = this.bestSuggestion(suggestions);
      if (!best) {
        this.trackLookupFail(domain);
        this.commit({ ...this.draft(), step: 'manual' });
        return;
      }
      await this.applySuggestion(best, domain);
    } catch {
      this.trackLookupFail(domain);
      this.commit({ ...this.draft(), step: 'manual' });
    } finally {
      this.finding.set(false);
    }
  }

  async selectSuggestion(suggestion: BrandSuggestionResponse): Promise<void> {
    const domain = normalizeDomain(suggestion.domain);
    if (!domain) {
      this.error.set('Enter your website to continue.');
      return;
    }
    this.finding.set(true);
    this.error.set('');
    this.commit({ ...this.draft(), url: domain }, false);
    try {
      await this.applySuggestion(suggestion, domain);
    } catch {
      this.trackLookupFail(domain);
      this.commit({ ...this.draft(), step: 'manual' });
    } finally {
      this.finding.set(false);
    }
  }

  submitManual(name: string, industry: string): void {
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      this.error.set('Enter your brand name.');
      return;
    }
    if (!industry) {
      this.error.set('Choose an industry.');
      return;
    }
    const domain = normalizeDomain(this.draft().url);
    const currency = this.draft().currency ?? detectCampaignCurrency(domain, this.draft().brand?.countryCode).currency;
    const brand: ValueFirstBrand = {
      name: trimmedName,
      domain,
      industry,
      description: this.draft().brand?.description ?? '',
      logoUrl: this.draft().brand?.logoUrl,
      bannerUrl: this.draft().brand?.bannerUrl,
      socials: this.draft().brand?.socials ?? [],
    };
    this.error.set('');
    this.commit({ ...this.draft(), brand, currency, step: 'found' });
  }

  async sendCode(honeypot: string, resend = false): Promise<boolean> {
    const email = this.draft().email.trim();
    if (honeypot.trim()) {
      return false;
    }
    if (!isValidWorkEmail(email)) {
      this.error.set('Enter a valid work email address.');
      return false;
    }
    if (isBlockedEmailDomain(email)) {
      this.error.set('Use your work email address to continue.');
      return false;
    }
    if (this.resendCooldown() > 0 || this.sendingCode()) {
      return false;
    }
    this.sendingCode.set(true);
    this.error.set('');
    try {
      await firstValueFrom(
        this.http.post(`${environment.shout_api.url}/auth/send-email-otp`, {
          email,
          turnstileToken: this.turnstileToken() ?? undefined,
          honeypot: honeypot || undefined,
        }),
      );
      this.commit({ ...this.draft(), email, authMode: 'email', step: 'code' });
      this.track(resend ? 'code_resend' : 'code_sent', {
        variant: VALUE_FIRST_VARIANT,
      });
      this.startCooldown();
      return true;
    } catch (error) {
      this.error.set(this.readErrorMessage(error, 'Failed to send code'));
      return false;
    } finally {
      this.sendingCode.set(false);
    }
  }

  async verifyCode(code: string): Promise<boolean> {
    const digits = code.replace(/\D/g, '').slice(0, 6);
    if (digits.length !== 6 || this.verifying()) {
      return false;
    }
    this.verifying.set(true);
    this.error.set('');
    try {
      const result = await firstValueFrom(
        this.http.post<{ verificationToken: string }>(`${environment.shout_api.url}/auth/verify-email-otp`, {
          email: this.draft().email.trim(),
          otp: digits,
        }),
      );
      this.verificationToken.set(result.verificationToken);
      this.track('code_verified', { variant: VALUE_FIRST_VARIANT });
      this.commit({ ...this.draft(), authMode: 'email', step: 'account' });
      return true;
    } catch (error) {
      this.error.set(this.readErrorMessage(error, 'Invalid code'));
      return false;
    } finally {
      this.verifying.set(false);
    }
  }

  async continueWithGoogle(): Promise<void> {
    this.track('sso_click', {
      variant: VALUE_FIRST_VARIANT,
      provider: 'google',
    });
    this.registering.set(true);
    this.error.set('');
    this.firebase.beginDeferPostLoginBootstrap();
    let outcome: RegistrationResult = 'error';
    let message = '';
    try {
      const result = await firstValueFrom(this.firebase.signInWithGoogle());
      const user = (result as { user?: GoogleUser }).user;
      if (!user?.uid || !user.email) {
        message = 'Google did not return an email address.';
        outcome = 'error';
      } else {
        const names = splitPersonName(user.displayName);
        this.googleUid.set(user.uid);
        this.googlePhotoUrl.set(user.photoURL ?? '');
        this.commit({
          ...this.draft(),
          email: user.email,
          firstName: names.firstName || this.draft().firstName,
          lastName: names.lastName || this.draft().lastName,
          authMode: 'google',
        }, false);
        if (!names.firstName || !names.lastName) {
          outcome = 'need-name';
        } else {
          outcome = await this.createAccount({
            firebaseUid: user.uid,
            email: user.email,
            firstName: names.firstName,
            lastName: names.lastName,
            photoURL: user.photoURL ?? '',
            password: null,
          });
          message = this.error();
        }
      }
    } catch (error) {
      outcome = this.isExistingAccount(error) ? 'exists' : 'error';
      message = this.readErrorMessage(error, 'Google sign-in failed.');
    } finally {
      this.firebase.endDeferPostLoginBootstrap();
      this.registering.set(false);
    }
    await this.finishRegistration(outcome, message);
  }

  async submitAccount(password: string): Promise<void> {
    const requirePassword = this.draft().authMode !== 'google';
    const fieldError = accountFieldError({
      firstName: this.draft().firstName,
      lastName: this.draft().lastName,
      password,
      requirePassword,
    });
    if (fieldError) {
      this.error.set(fieldError);
      return;
    }
    if (requirePassword && !this.verificationToken()) {
      this.error.set('Verify your email code before creating the account.');
      this.commit({ ...this.draft(), step: 'code' });
      return;
    }
    this.registering.set(true);
    this.error.set('');
    this.firebase.beginDeferPostLoginBootstrap();
    let outcome: RegistrationResult = 'error';
    try {
      if (requirePassword) {
        const credential = await firstValueFrom(
          this.firebase.signUp(this.draft().email.trim(), password),
        );
        const uid = credential.user?.uid;
        if (!uid) {
          outcome = 'error';
          this.error.set('We could not create your login.');
        } else {
          outcome = await this.createAccount({
            firebaseUid: uid,
            email: this.draft().email.trim(),
            firstName: this.draft().firstName.trim(),
            lastName: this.draft().lastName.trim(),
            photoURL: '',
            password,
          });
        }
      } else {
        const uid = this.googleUid();
        if (!uid) {
          outcome = 'error';
          this.error.set('Google sign-in expired. Continue with Google again.');
          this.commit({ ...this.draft(), step: 'email', authMode: 'email' }, false);
        } else {
          outcome = await this.createAccount({
            firebaseUid: uid,
            email: this.draft().email.trim(),
            firstName: this.draft().firstName.trim(),
            lastName: this.draft().lastName.trim(),
            photoURL: this.googlePhotoUrl(),
            password: null,
          });
        }
      }
    } catch (error) {
      outcome = this.isExistingAccount(error) ? 'exists' : 'error';
      if (outcome === 'error' && !this.error()) {
        this.error.set(this.readErrorMessage(error, 'We could not finish creating your account. Please try again.'));
      }
    } finally {
      this.firebase.endDeferPostLoginBootstrap();
      this.registering.set(false);
    }
    await this.finishRegistration(outcome, this.error());
  }

  async finish(choice: 'quick_brief' | 'skip'): Promise<void> {
    this.track('start_choice', {
      variant: VALUE_FIRST_VARIANT,
      choice,
    });
    this.track('goals_selected', {
      variant: VALUE_FIRST_VARIANT,
      goals: this.draft().goals.join(','),
    });
    if (choice === 'quick_brief') {
      this.track('onboarding_quick_brief');
      await this.router.navigate(['/onboarding-quick-brief']);
      return;
    }
    this.currentUser.onboardingComplete.set(true);
    this.currentUser.checkBrandComplete();
    await this.router.navigate(['/dashboard']);
  }

  private async finishRegistration(outcome: RegistrationResult, message: string): Promise<void> {
    if (outcome === 'ok') {
      await this.firebase.syncClaimsAndRefreshToken();
      if (this.draft().authMode === 'google') {
        await this.firebase.reloadCurrentUser();
      }
      this.firebase.runPostLoginBootstrapForCurrentUser();
      this.error.set('');
      this.commit({ ...this.draft(), step: 'welcome' });
      return;
    }
    if (outcome === 'need-name') {
      this.error.set('');
      this.commit({ ...this.draft(), authMode: 'google', step: 'account' });
      return;
    }
    if (outcome === 'exists') {
      const login = await this.alert.alert({
        title: 'Login?',
        message: 'Looks like you already have an account with this email address. Would you like to login?',
        icon: 'info',
        confirmText: 'Login',
        cancelText: 'Cancel',
        type: 'confirm',
      });
      if (login) {
        await this.router.navigate(['/login']);
      }
      return;
    }
    await this.alert.alert({
      title: 'Something went wrong',
      message: message || 'We could not finish creating your account. Please try again.',
      icon: 'error',
      type: 'alert',
      confirmText: 'Okay',
      cancelText: 'Close',
    });
  }

  private async createAccount(input: {
    firebaseUid: string;
    email: string;
    firstName: string;
    lastName: string;
    photoURL: string;
    password: string | null;
  }): Promise<RegistrationResult> {
    const personalInfo: CreateUserDto = {
      firebaseUid: input.firebaseUid,
      email: input.email,
      firstName: input.firstName,
      lastName: input.lastName,
      role: UserRolesEnum['Brand Admin'],
      jobPosition: 'unknown',
      photoURL: input.photoURL,
      isActive: true,
      hubspotUserToken: this.hubspotToken(),
      verificationToken: input.password ? (this.verificationToken() ?? '') : undefined,
    };
    try {
      const createUserResult = await firstValueFrom(
        this.http.post(`${environment.shout_api.url}/business-accounts/onboarding-brand`, personalInfo),
      );
      if (!createUserResult) {
        this.error.set('We could not create your account.');
        return 'error';
      }
      this.currentUser.setShoutUser(createUserResult);
      this.ga.identifyAnalyticsUser(input.firebaseUid, {
        email: input.email,
        name: `${input.firstName} ${input.lastName}`.trim(),
        created_at: new Date().toISOString(),
      });
      this.posthog.posthog.identify(input.firebaseUid, {
        email: input.email,
        name: `${input.firstName} ${input.lastName}`.trim(),
        created_at: new Date().toISOString(),
      });
      this.posthog.posthog.capture('user_signed_up', {
        firebase_uid: input.firebaseUid,
        auth_mode: this.draft().authMode,
        ...this.marketingAttribution.toAcquisitionProperties(),
      });
      const brand = this.draft().brand;
      const images = await mirrorBrandImages(this.uploader, brand?.logoUrl, brand?.bannerUrl);
      const businessInfo: CreateBusinessAccountDto = {
        companyName: brand?.name || this.fallbackBrandName() || 'No name...',
        website: brand?.domain || normalizeDomain(this.draft().url) || 'No website...',
        instagram: this.socialUrl('instagram'),
        tiktok: this.socialUrl('tiktok'),
        about: brand?.description || '',
        coverPhoto: images.coverPhoto,
        companyLogo: images.companyLogo,
        creatorId: input.firebaseUid,
        isActive: true,
        currencyCode: this.activeCurrency().toLowerCase(),
        isPayg: false,
        industries: [{
          emoji: '',
          id: brand?.domain || 'manual',
          name: brand?.industry || brand?.name || 'Brand',
          slug: brand?.domain || 'manual',
          score: 0,
        }],
        vatNumber: '',
      };
      const created = await firstValueFrom(
        this.http.post<{ user?: unknown; business?: unknown }>(
          `${environment.shout_api.url}/business-accounts/associate-brand-to-user`,
          businessInfo,
        ),
      );
      if (created?.user) {
        this.currentUser.setShoutUser(created.user);
      }
      if (created?.business) {
        this.currentUser.setFocussedBrand(created.business, this.firebase.accessToken);
      }
      const createdBusinessId = (created?.business as { id?: string } | undefined)?.id;
      void this.adConversions.saveFirstTouch('brand', createdBusinessId);
      this.adConversions.trackSignup('brand', createdBusinessId);
      this.track('brand_account_created', {
        variant: VALUE_FIRST_VARIANT,
        firebase_uid: input.firebaseUid,
      });
      return 'ok';
    } catch (error) {
      if (this.isExistingAccount(error)) {
        return 'exists';
      }
      this.error.set(this.readErrorMessage(error, 'We could not finish creating your account. Please try again.'));
      return 'error';
    }
  }

  private async applySuggestion(suggestion: BrandSuggestionResponse, domain: string): Promise<void> {
    let brand = brandFromSuggestion(suggestion);
    if (suggestion.brandId) {
      try {
        const detail = await firstValueFrom(this.brandFetch.getBrandDetails(suggestion.brandId));
        console.log('brand suggestion detail', detail);
        brand = brandFromBrandfetch(detail, brand);
      } catch {
        // Search hit is enough to show the workspace. Details are optional.
      }
    }
    const currency = this.draft().currency ?? detectCampaignCurrency(brand.domain || domain, brand.countryCode).currency;
    this.track('brand_lookup_success', {
      variant: VALUE_FIRST_VARIANT,
      domain,
    });
    this.commit({ ...this.draft(), brand, currency, step: 'found' });
  }

  private bestSuggestion(suggestions: BrandSuggestionResponse[] | null | undefined): BrandSuggestionResponse | null {
    if (!suggestions || suggestions.length === 0) {
      return null;
    }
    return [...suggestions].sort((left, right) => (right._score ?? 0) - (left._score ?? 0))[0];
  }

  private socialUrl(type: string): string {
    const match = this.draft().brand?.socials.find((social) => social.type.toLowerCase().includes(type));
    return match?.url ?? '';
  }

  private fallbackBrandName(): string {
    const domain = normalizeDomain(this.draft().url);
    const label = domain.split('.')[0] ?? '';
    if (!label) {
      return '';
    }
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  private trackLookupFail(domain: string): void {
    this.track('brand_lookup_fail', {
      variant: VALUE_FIRST_VARIANT,
      domain,
    });
    this.track('manual_fallback', {
      variant: VALUE_FIRST_VARIANT,
      domain,
    });
  }

  private commit(next: ValueFirstDraft, trackStep = true): void {
    const previous = this.draft().step;
    this.draft.set(next);
    this.writeSession(next);
    if (trackStep && next.step !== previous) {
      this.trackStepView(next.step);
    }
  }

  private trackStepView(step: ValueFirstStep): void {
    this.track('onboarding_step_view', {
      variant: VALUE_FIRST_VARIANT,
      step,
      step_index: onboardingStepIndex(ONBOARDING_FLOW.brand, step),
    });
  }

  private track(event: string, properties?: Record<string, unknown>): void {
    this.ga.trackOnboardingEvent(event, {
      flow: ONBOARDING_FLOW.brand,
      ...properties,
    });
  }

  private writeSession(draft: ValueFirstDraft): void {
    const storage = this.sessionStorage();
    if (!storage) {
      return;
    }
    try {
      storage.setItem(VALUE_FIRST_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // Ignore private-mode storage failures. The in-memory draft still works.
    }
  }

  private sessionStorage(): Storage | undefined {
    return this.document.defaultView?.sessionStorage;
  }

  private hubspotToken(): string {
    const cookies = this.document.cookie.split(';');
    for (const raw of cookies) {
      const cookie = raw.trim();
      if (cookie.startsWith('hubspotutk=')) {
        return cookie.substring('hubspotutk='.length);
      }
    }
    return '';
  }

  private startCooldown(): void {
    this.clearCooldown();
    this.resendCooldown.set(60);
    this.cooldownTimer = setInterval(() => {
      const next = this.resendCooldown() - 1;
      if (next <= 0) {
        this.resendCooldown.set(0);
        this.clearCooldown();
        return;
      }
      this.resendCooldown.set(next);
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  private isExistingAccount(error: unknown): boolean {
    const code = (error as { code?: string }).code;
    if (code === 'auth/email-already-in-use') {
      return true;
    }
    if (error instanceof HttpErrorResponse && (error.status === 409 || error.status === 400)) {
      return this.readErrorMessage(error, '').toLowerCase().includes('already');
    }
    return false;
  }

  private readErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const message = (error.error as { message?: unknown } | null)?.message ?? fallback;
      if (Array.isArray(message)) {
        return String(message[0] ?? fallback);
      }
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }
}
